import React, { useEffect, useState } from 'react';
import { Link } from 'gatsby';
import { Box, Button, Stack, Typography } from '@mui/material';
import { DynamicContentService } from '../../services/dynamic-content';
import { useI18next } from 'gatsby-plugin-react-i18next';

export interface ContentCardProps {
  alignImageRight: boolean;
  alt: string;
  callToAction_en: string;
  callToAction_es: string;
  contentCardId: string;
  detailsLink: string;
  detailsText_es: string;
  detailsText_en: string;
  externalEmbed: string;
  htmlContent_en: string;
  htmlContent_es: string;
  imageAlignment: string;
  link: string;
  mainImage: { url: string };
  primaryColor: string;
  secondaryColor: string;
  title_en: string;
  title_es: string;
  video: string;
  videoText: string;
}

const dynamicContentService = new DynamicContentService();

interface Props {
  contentCardId: string;
}

export const ContentCard = ({ contentCardId }: Props) => {
  const { language: locale } = useI18next();

  const [contentCard, setContentCard] = useState<ContentCardProps | null>(null);

  const fetchCardImage = async () => {
    /**
     * Fetch complete data of content card.
     * This process is useful to know if the content card is empty
     */
    const contentCard = await dynamicContentService.getComponent('contentcards', contentCardId);
    if (!contentCard) return;
    setContentCard(contentCard);
  };

  useEffect(() => {
    fetchCardImage();
  }, []);

  /**
   *  If there is no card content, should not be rendered.
   */
  if (!contentCard) return null;

  const title = contentCard[`title_${locale}`];
  const detailsLink = contentCard.detailsLink;
  const detailsText = contentCard[`detailsText_${locale}`];
  const htmlContent = contentCard[`htmlContent_${locale}`];
  const callToAction = contentCard[`callToAction_${locale}`];

  return (
    <Stack width="100%" bgcolor={contentCard.primaryColor || '#FFFFFF'}>
      <Stack
        flexDirection={{ xs: 'column-reverse', md: 'row' }}
        alignItems="center"
        gap={{ xs: 4, md: 10 }}
        className="container"
        borderRadius={2}
        padding="2em"
        py={6}
      >
        <img
          src={contentCard.mainImage.url}
          alt={contentCard.alt}
          style={{
            width: '100%',
            maxWidth: '420px',
            height: '430px',
            borderRadius: '12px',
            alignSelf: 'center',
            display: 'flex',
            objectFit: 'cover',
          }}
        />

        <Stack width="100%" px={{ xs: 2, md: 0 }}>
          <Stack flexDirection="row" justifyContent="space-between" alignItems="center" gap={2} mb={2.5}>
            <Typography color="primary.main" fontSize={32} fontWeight={700}>
              {title}
            </Typography>

            {detailsLink && detailsText ? (
              <Link to={detailsLink}>
                <Typography color="primary.light" fontWeight={700} noWrap>
                  {detailsText}
                </Typography>
              </Link>
            ) : null}
          </Stack>

          <Box
            sx={{ '& ul': { listStyle: 'none', '& li': { marginBottom: 2.5 } } }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />

          {contentCard.link && callToAction ? (
            <Link to={contentCard.link}>
              <Button variant="contained" size="large" sx={{ width: 200, mt: 2.5 }}>
                {callToAction}
              </Button>
            </Link>
          ) : null}
        </Stack>
      </Stack>
    </Stack>
  );
};
